import React, { useEffect, useState } from 'react';
import SectionHeader from '../../components/common/SectionHeader';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Container,
} from '@mui/material';
import { IoGiftOutline, IoCartOutline } from 'react-icons/io5';
import { environment } from '../../../environments/environment';
import { auth } from '../../../../../../libs/firebase-service/src';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { CiForkAndKnife, CiLocationOn } from 'react-icons/ci';
import { IoCheckmarkOutline } from 'react-icons/io5';
import noItems from '../../../assets/Hamburger-pana 1.png';
const Deals = () => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.authReducer.userData);
  const stores = useSelector((state) => state.shopReducer.stores);
  const [loyaltyData, setLoyaltyData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Reward tiers from loyalty program (if available)
  const rewardTiers = loyaltyData?.message?.program?.rewardTiers || [];
  // Resolve applied locations (branch names) from loyalty program
  const programLocationIds = loyaltyData?.message?.program?.locationIds;
  const appliedLocations = React.useMemo(() => {
    if (!programLocationIds) return 'All branches';
    if (Array.isArray(programLocationIds) && programLocationIds.length === 0)
      return 'All branches';

    // Map IDs to store names using stores from redux; fall back to ID if name not found
    const mapped = (programLocationIds || [])
      .map((id) => {
        const s = (stores || []).find(
          (st) => st?.id === id || st?.storeId === id
        );
        return s ? s.name : id;
      })
      .filter(Boolean);

    return mapped.length > 0 ? mapped.join(', ') : 'All branches';
  }, [programLocationIds, stores]);

  useEffect(() => {
    const fetchLoyaltyData = async () => {
      try {
        setError(null);
const loyaltyProgramId =
          environment.restaurantId === 'fattyPattyDev' 
            ? '832fe29b-3700-4977-ad33-be88f6aa9a54'
            : 'a66ddd7b-362d-4fea-ab5b-dd06974aaf21';
        // Phone number is optional for this endpoint. If missing, still fetch using other identifiers.

        // Get authentication token
        const token = await auth?.currentUser?.getIdToken();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: token,
        };

        // Remove leading '+' from phone number if present and only include it when available
        const sanitizedPhoneNumber = userData?.phoneNumber
          ? String(userData.phoneNumber).replace(/^\+/, '')
          : null;

        // Note: parameters are sent as query string, no body is used
        const params = {
          restaurantId: environment.restaurantId,
          programId: loyaltyProgramId,
        };

        if (sanitizedPhoneNumber) {
          params.phoneNumber = sanitizedPhoneNumber;
        }

        const query = new URLSearchParams(params).toString();

        const responseRaw = await fetch(
          `${environment.apiUrl}/loyalty/accounts?${query}`,
          {
            method: 'GET',
            headers,
          }
        );

        const response = await responseRaw.json();

        if (response) {
          setLoyaltyData(response);
        }
      } catch (err) {
        console.error('Error fetching loyalty data:', err);
        setError('Failed to load loyalty data');
      } finally {
        setLoading(false);
      }
    };

    fetchLoyaltyData();
  }, [userData]);

  if (loading) {
    return (
      <Box
        sx={{
          py: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '80vh',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: environment.mainColor }} />
        <Typography sx={{ mt: 2, color: '#4A5565' }}>
          Loading deals...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          py: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ color: '#ef4444', fontWeight: 600, mb: 1 }}>
          {error}
        </Typography>
        <Typography sx={{ color: '#4A5565' }}>
          Please try again later
        </Typography>
      </Box>
    );
  }

  if (!loyaltyData) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: 'center', py: 8 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={noItems}
            alt="No items available"
            style={{
              maxWidth: '363px',
              width: '100%',
              height: 'auto',
              marginBottom: '20px',
              objectFit: 'cover',
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              marginBottom: '8px',
              color: '#172B4D',
              fontSize: {
                xs: '20px',
                md: '28px',
                lg: '40px',
              },
              fontFamily: 'Jost, sans-serif',
              textAlign: 'center',
              textTransform: 'capitalize',
            }}
          >
            No deals available
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#828282',
              maxWidth: '300px',
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            Please check back later for exciting deals and offers!
          </Typography>
        </div>
      </Container>
    );
  }
  return (
    <section>
      <Box>
        <SectionHeader
          title="deals"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Deals', current: true },
          ]}
          fontFamily="Jost"
          showBreadcrumbs={true}
          titleVariant="h2"
          customStyles={{
            title: { fontSize: '40px', fontWeight: '700' },
            container: { minHeight: '250px' },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: 8, md: -4 },
            mb: 8,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '763px',
              px: 2,
              boxSizing: 'border-box',
            }}
          >
            {/* White rounded card */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '16px',
                boxShadow: ' 0px 4px 100px 0px #00000014',
                py: { xs: 6, md: 8 },
                px: { xs: 3, md: 6 },
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  top: 0,
                  width: '79px',
                  height: '79px',
                  borderRadius: '27px',
                  bgcolor: environment.mainColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IoGiftOutline size={40} style={{ color: 'white' }} />
              </Box>

              <Typography
                component="h2"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: { xs: '28px', md: '48px' },
                  maxWidth: '650px',
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: '0%',
                  mt: 3,
                  mb: 2,
                  color: '#515151',
                }}
              >
                Exclusive Deals & Rewards
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  maxWidth: '500px',
                  mx: 'auto',
                  fontSize: { xs: '12px', md: '14px' },
                  color: '#9F9F9F',
                }}
              >
                Discover Amazing Ways To Earn And Redeem Points With Square
                Loyalty. More Rewards, More Savings, More Reasons To Love
                Shopping With Us.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Featured Offers */}
        <Box
          variant="section"
          sx={{ mt: { xs: 6, md: 10 }, px: { xs: 2, md: 17 }, mb: 8 }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: { xs: '20px', md: '28px' },
              fontWeight: 700,
              color: '#172B4D',
              mb: 3,
            }}
          >
            Featured Offers
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {rewardTiers &&
              rewardTiers.length > 0 &&
              rewardTiers?.map((tier, idx) => (
                <Box
                  key={idx}
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: '12px',
                    p: { xs: 3, md: 4 },
                    boxShadow: '0 6px 20px rgba(16,24,40,0.06)',
                    minHeight: '293px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: '1px solid #E3E3E3',
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: '43px',
                            height: '43px',
                            borderRadius: '8px',
                            bgcolor: environment.mainColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IoGiftOutline size={18} color="white" />
                        </Box>
                      </Box>

                      {/* <Box
                      sx={{
                        bgcolor: '#fff5f1',
                        color: environment.mainColor,
                        px: 1.8,
                        py: 0.6,
                        borderRadius: 10,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {card.badge}
                    </Box> */}
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: '#172B4D',
                        mb: 1,
                        fontSize: '18px',
                        fontFamily: 'Jost',
                      }}
                    >
                      {tier?.name}
                    </Typography>

                    {/* Enhanced branch locations display */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1.5,
                        p: 1.5,
                        bgcolor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                      }}
                    >
                      <Box
                        sx={{
                          width: '25px',
                          height: '25px',
                          borderRadius: '50%',
                          bgcolor: environment.mainColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.2,
                        }}
                      >
                        <CiLocationOn size={18} color="white" />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#495057',
                            fontSize: '12px',
                            fontFamily: 'Jost',
                            fontWeight: 600,
                            lineHeight: 1.3,
                            mb: 0.3,
                          }}
                        >
                          Available Locations
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6c757d',
                            fontSize: '11px',
                            fontFamily: 'Jost',
                            fontWeight: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {appliedLocations}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Enhanced per-reward product applicability using tier data */}
                    {(() => {
                      // Use the tier's productDetails directly instead of separate rewards
                      const productDetails = tier?.productDetails || [];
                      const productType = tier?.productType || 'all';

                      if (
                        productType === 'all' ||
                        tier?.productSet?.productSetData?.allProducts === true
                      ) {
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              p: 1.5,
                              bgcolor: '#e8f5e8',
                              borderRadius: '8px',
                              border: '1px solid #c3e6c3',
                            }}
                          >
                            <Box
                              sx={{
                                width: '25px',
                                height: '25px',
                                borderRadius: '50%',
                                bgcolor: '#28a745',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.2,
                              }}
                            >
                              <IoCheckmarkOutline size={18} color="white" />
                            </Box>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#155724',
                                  fontSize: '12px',
                                  fontFamily: 'Jost',
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                  mb: 0.3,
                                }}
                              >
                                Item Coverage
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#155724',
                                  fontSize: '11px',
                                  fontFamily: 'Jost',
                                  fontWeight: 500,
                                  lineHeight: 1.4,
                                }}
                              >
                                All menu items eligible
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }

                      // Use productDetails array which contains the actual names and types
                      if (
                        Array.isArray(productDetails) &&
                        productDetails.length > 0
                      ) {
                        // Group by type for better display
                        const categories = productDetails
                          .filter(
                            (detail) =>
                              detail.type === 'category' ||
                              detail.rawType === 'CATEGORY'
                          )
                          .map((detail) => detail.name);

                        const items = productDetails
                          .filter(
                            (detail) =>
                              detail.type === 'item_variation' ||
                              detail.rawType === 'ITEM_VARIATION'
                          )
                          .map((detail) => detail.name);

                        // Combine results with labels
                        const allMatched = [];
                        if (categories.length > 0) {
                          allMatched.push(
                            `Categories: ${categories.join(', ')}`
                          );
                        }
                        if (items.length > 0) {
                          allMatched.push(`Items: ${items.join(', ')}`);
                        }

                        const displayText =
                          allMatched.length > 0
                            ? allMatched.join(' • ')
                            : productDetails.map((d) => d.name).join(', ');
                        const isLongList = displayText.length > 80;
                        const truncatedText = isLongList
                          ? displayText.substring(0, 80) + '...'
                          : displayText;

                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              p: 1.5,
                              bgcolor: '#fff3cd',
                              borderRadius: '8px',
                              border: '1px solid #ffeaa7',
                            }}
                          >
                            <Box
                              sx={{
                                width: '25px',
                                height: '25px',
                                borderRadius: '50%',
                                bgcolor: '#ffc107',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.2,
                              }}
                            >
                              <CiForkAndKnife
                                size={18}
                                style={{ color: 'white' }}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#856404',
                                  fontSize: '12px',
                                  fontFamily: 'Jost',
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                  mb: 0.3,
                                }}
                              >
                                {categories.length > 0 && items.length > 0
                                  ? 'Select Categories & Items'
                                  : categories.length > 0
                                  ? 'Select Categories'
                                  : 'Select Items Only'}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#856404',
                                  fontSize: '11px',
                                  fontFamily: 'Jost',
                                  fontWeight: 500,
                                  lineHeight: 1.4,
                                }}
                                title={isLongList ? displayText : undefined}
                              >
                                {truncatedText}
                                {isLongList && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: '#6c757d',
                                      fontSize: '10px',
                                      fontStyle: 'italic',
                                      ml: 0.5,
                                    }}
                                  >
                                    ({categories.length + items.length} total)
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }

                      // Default fallback - no product details or not applicable
                      return null;
                    })()}

                    {/* <Typography
                    variant="body2"
                    sx={{
                      color: '#7D7C7C',
                      fontSize: '14px',
                      fontFamily: 'Jost',
                      fontWeight: 500,
                    }}
                  >
                    {card.desc}
                  </Typography> */}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        color: environment.mainColor,
                        fontWeight: 700,
                        fontSize: '17px',
                        fontFamily: 'Jost',
                      }}
                    >
                      {tier.points} PTS
                    </Typography>
                    <Button
                      size="small"
                      sx={{
                        bgcolor: environment.mainColor,
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '14px',
                        fontFamily: 'Jost, sans-serif',
                        borderRadius: '52px',
                        textTransform: 'none',
                        p: '9px 16px',
                        '&:hover': { bgcolor: environment.mainColor },
                      }}
                      onClick={() => navigate('/menu', { state: { openBranchDialog: true } })}
                    >
                      order now
                    </Button>
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      </Box>

      {/* How to earn points */}
      {loyaltyData?.message?.program?.accrualRules?.length > 0 && (
        <Box
          component="section"
          sx={{ mb: 8, bgcolor: '#FFEBE4', py: { xs: 6, md: 10 } }}
        >
          <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 } }}>
            <Typography
              sx={{
                textAlign: 'center',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '20px', md: '28px', lg: '36px' },
                color: '#353535',
                mb: 1,
                textTransform: 'uppercase',
              }}
            >
              how to earn points
            </Typography>

            <Typography
              sx={{
                textAlign: 'center',
                color: '#9F9F9F',
                fontSize: { xs: 11, md: 13 },
                maxWidth: '600px',
                mx: 'auto',
                mb: 8,
              }}
            >
              Discover Amazing Ways To Earn And Redeem Points With Square
              Loyalty. More Rewards, More Savings, More Reasons To Love Shopping
              With Us.
            </Typography>

            <Box
              sx={{
                // display: 'grid',
                // gridTemplateColumns: {
                //   xs: '1fr',
                //   sm: 'repeat(2, 1fr)',
                //   md: 'repeat(4, 1fr)',
                // },
                // gap: { xs: 3, md: 4 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Dynamic accrual rules from loyalty program when available */}
              {(() => {
                const accrualRules =
                  loyaltyData?.message?.program?.accrualRules || [];

                const formatMoney = (amountStr) => {
                  if (!amountStr) return '0.00';
                  const amount = parseInt(amountStr, 10);
                  if (isNaN(amount)) return amountStr;
                  // assume amount is in cents
                  return (amount / 100).toFixed(2);
                };

                if (accrualRules.length > 0) {
                  return accrualRules.map((rule, i) => {
                    const type = rule.accrualType || 'SPEND';
                    const points = rule.points || 0;
                    let title = '';
                    let description = '';
                    let badge = null;

                    if (type === 'VISIT') {
                      title = 'Visit-based accrual';
                      const minAmount =
                        rule.visitData?.minimumAmountMoney?.amount;
                      const minFormatted = formatMoney(minAmount);
                      description = `Earn ${points} point${
                        points === 1 ? '' : 's'
                      } for each visit`;
                      if (minAmount && parseInt(minAmount, 10) > 0) {
                        description += ` with a minimum purchase of ${environment.currency}${minFormatted}.`;
                      } else {
                        description += '.';
                      }
                      badge = <CiLocationOn size={22} color="white" />;
                    } else if (type === 'SPEND') {
                      title = 'Amount-spent accrual';
                      // Default: interpret "points" as points per dollar
                      description = `Earn ${points || 1} point${
                        (points || 1) === 1 ? '' : 's'
                      } for each $1 spent.`;
                      badge = <IoCartOutline size={22} color="white" />;
                    } else {
                      title = `${type.toLowerCase()} accrual`;
                      description = rule.description || JSON.stringify(rule);
                      badge = <IoGiftOutline size={22} color="white" />;
                    }

                    return (
                      <Box
                        key={`${type}-${i}`}
                        sx={{
                          position: 'relative',
                          bgcolor: '#FFFFFF',
                          borderRadius: '43px',
                          p: { xs: 4, md: 5 },
                          pt: { xs: 6, md: 7 },
                          boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minHeight: '205px',
                          textAlign: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            top: 0,
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            bgcolor: environment.mainColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                          }}
                        >
                          {badge}
                        </Box>

                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontFamily: 'Jost',
                            fontSize: { xs: 16, md: 20 },
                            color: '#172B4D',
                            mb: 1,
                            textTransform: 'capitalize',
                          }}
                        >
                          {title}
                        </Typography>

                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#172B4D',
                              mb: 0.5,
                              fontSize: '13px',
                              fontFamily: 'Jost',
                            }}
                          >
                            {description}
                          </Typography>

                          <Typography
                            sx={{
                              color: environment.mainColor,
                              fontWeight: 700,
                              fontSize: '20px',
                              fontFamily: 'Jost',
                            }}
                          >
                            {type === 'VISIT'
                              ? `${points} PTS / visit`
                              : `${points || 1} PTS / $1`}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  });
                }

                // Return null so the section stays hidden when no data is available
                return null;
              })()}
            </Box>
          </Box>
        </Box>
      )}
    </section>
  );
};

export default Deals;
